import React, { useState, useRef } from 'react';
import { ArrowLeft, Shield, AlertTriangle, List, Filter, Key } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';

const WafPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { wafData, region, accountName, duration } = location.state || {};

  const LongTextCell = ({ text, maxLength = 30 }) => {
    const [showTooltip, setShowTooltip] = useState(false);
    const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
    const tooltipRef = useRef(null);
    const cellRef = useRef(null);
    
    if (!text || text.length <= maxLength) {
      return <span>{text}</span>;
    }
    
    const handleMouseEnter = (e) => {
      setShowTooltip(true);
      setTimeout(() => {
        if (cellRef.current && tooltipRef.current) {
          const cellRect = cellRef.current.getBoundingClientRect();
          const tooltipRect = tooltipRef.current.getBoundingClientRect();
          const viewportHeight = window.innerHeight;
          const spaceBelow = viewportHeight - cellRect.bottom;
          const tooltipHeight = tooltipRect.height;
          
          if (spaceBelow < tooltipHeight) {
            setTooltipPosition({
              top: -tooltipHeight - 5,
              left: 0
            });
          } else {
            setTooltipPosition({
              top: "100%",
              left: 0
            });
          }
        }
      }, 0);
    };
  
    return (
      <div className="relative inline-block">
        <div
          ref={cellRef}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={() => setShowTooltip(false)}
          className="cursor-pointer border-b border-dotted border-gray-400"
        >
          {text.substring(0, maxLength)}...
        </div>
        {showTooltip && (
          <div 
            ref={tooltipRef}
            className="absolute z-50 w-64 p-2 text-sm bg-gray-900 text-white rounded shadow-lg"
            style={{
              top: tooltipPosition.top,
              left: tooltipPosition.left,
              maxWidth: "250px",
              wordBreak: "break-word"
            }}
          >
            {text}
          </div>
        )}
      </div>
    );
  };

  // Rule names popup component - Updated styling
  const RuleCountCell = ({ count, ruleNames }) => {
    const [showRules, setShowRules] = useState(false);
    const cellRef = useRef(null);
    
    if (!count || !ruleNames) {
      return <span>{count || 0}</span>;
    }
    
    // Parse rule names into array
    const rulesArray = ruleNames.split(', ');
    
    return (
      <div className="relative inline-block">
        <div
          ref={cellRef}
          onClick={() => setShowRules(!showRules)}
          className="cursor-pointer font-medium text-blue-600 hover:underline"
        >
          {count}
        </div>
        {showRules && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50"
            onClick={() => setShowRules(false)}
          >
            <div 
              className="bg-white rounded-lg shadow-xl p-4 max-w-md w-full m-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center pb-2 border-b border-gray-200 mb-3">
                <h3 className="font-medium text-[#003366]">Rule Names</h3>
                <button 
                  onClick={() => setShowRules(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>
              <div className="max-h-64 overflow-y-auto">
              <ul className="space-y-1">
                {rulesArray.map((rule, idx) => (
                <li key={idx} className="text-sm text-gray-700 py-1 border-b border-gray-100 text-left">
                  {rule}
                </li>
                  ))}
              </ul>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Calculate summary statistics
  const calculateSummary = (data) => {
    if (!data) return {};
    
    const summary = {
      webACLCount: new Set(),
      ruleCount: new Set(),
      defaultActions: {},
      scopeTypes: {},
      ruleNames: new Set()
    };

    data.forEach(event => {
      if (event.waf_id) summary.webACLCount.add(event.waf_id);
      if (event.waf_rule_count) summary.ruleCount.add(event.waf_rule_count);
      
      // Count default actions
      if (event.waf_default_action) {
        summary.defaultActions[event.waf_default_action] = (summary.defaultActions[event.waf_default_action] || 0) + 1;
      }
      
      // Count scope types
      if (event.waf_scope) {
        summary.scopeTypes[event.waf_scope] = (summary.scopeTypes[event.waf_scope] || 0) + 1;
      }

      // Collect rule names
      if (event.waf_rule_names) {
        const ruleNamesList = event.waf_rule_names.split(', ');
        ruleNamesList.forEach(rule => summary.ruleNames.add(rule));
      }
    });

    return {
      ...summary,
      webACLCount: summary.webACLCount.size,
      ruleCount: summary.ruleCount.size ? Math.max(...summary.ruleCount) : 0,
      uniqueRuleCount: summary.ruleNames.size
    };
  };

  const summary = calculateSummary(wafData?.data);
  const wafOperations = wafData?.summary || { 
    webACLOperations: { createCount: 0, deleteCount: 0, updateCount: 0 },
    ruleOperations: { createCount: 0, deleteCount: 0, updateCount: 0 },
    ruleGroupOperations: { createCount: 0, deleteCount: 0, updateCount: 0 },
    apiKeyOperations: { createCount: 0, deleteCount: 0, updateCount: 0 }
  };

  // Sample data for Rules, Rule Groups, and API Keys tables
  const ruleData = [];
  const ruleGroupData = [];
  const apiKeyData = [];

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button 
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <ArrowLeft className="h-6 w-6 text-gray-600" />
          </button>
          <h1 className="text-2xl font-bold text-[#003366]">WAF Activity Details</h1>
        </div>
        <div className="text-sm text-gray-500">
          Last {duration}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-lg p-6">
        <h2 className="text-xl font-semibold text-[#003366] mb-4">Activity Summary</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="text-sm text-gray-500">Total Web ACLs</div>
            <div className="text-xl font-semibold text-[#003366]">
              {summary.webACLCount || 0}
            </div>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="text-sm text-gray-500">Region</div>
            <div className="text-xl font-semibold text-[#003366]">
              {region}
            </div>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="text-sm text-gray-500">Account</div>
            <div className="text-xl font-semibold text-[#003366]">
              {accountName}
            </div>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="text-sm text-gray-500">Total Operations</div>
            <div className="text-xl font-semibold text-[#003366]">
              {wafOperations.webACLOperations.createCount + 
               wafOperations.webACLOperations.deleteCount + 
               wafOperations.webACLOperations.updateCount +
               wafOperations.ruleOperations.createCount +
               wafOperations.ruleOperations.deleteCount +
               wafOperations.ruleOperations.updateCount +
               wafOperations.ruleGroupOperations.createCount +
               wafOperations.ruleGroupOperations.deleteCount +
               wafOperations.ruleGroupOperations.updateCount +
               0} {/* API key operations sum is explicitly set to 0 */}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <Shield className="h-6 w-6 text-[#003366]" />
            <h3 className="text-lg font-semibold text-[#003366]">WebACL Operations</h3>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Created</span>
              <span className="font-semibold">{wafOperations.webACLOperations.createCount || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Updated</span>
              <span className="font-semibold">{wafOperations.webACLOperations.updateCount || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Deleted</span>
              <span className="font-semibold">{wafOperations.webACLOperations.deleteCount || 0}</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <List className="h-6 w-6 text-[#003366]" />
            <h3 className="text-lg font-semibold text-[#003366]">Rule Operations</h3>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Created</span>
              <span className="font-semibold">{wafOperations.ruleOperations.createCount || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Updated</span>
              <span className="font-semibold">{wafOperations.ruleOperations.updateCount || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Deleted</span>
              <span className="font-semibold">{wafOperations.ruleOperations.deleteCount || 0}</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <Filter className="h-6 w-6 text-[#003366]" />
            <h3 className="text-lg font-semibold text-[#003366]">Rule Group Operations</h3>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Created</span>
              <span className="font-semibold">{wafOperations.ruleGroupOperations.createCount || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Updated</span>
              <span className="font-semibold">{wafOperations.ruleGroupOperations.updateCount || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Deleted</span>
              <span className="font-semibold">{wafOperations.ruleGroupOperations.deleteCount || 0}</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <Key className="h-6 w-6 text-[#003366]" />
            <h3 className="text-lg font-semibold text-[#003366]">API Key Operations</h3>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Created</span>
              <span className="font-semibold">0</span> {/* Zeroed API key create count */}
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Updated</span>
              <span className="font-semibold">0</span> {/* Zeroed API key update count */}
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Deleted</span>
              <span className="font-semibold">0</span> {/* Zeroed API key delete count */}
            </div>
          </div>
        </div>
      </div>

      {/* Original WAF Events Table - Now moved below the operation cards */}
      <div className="bg-white rounded-lg shadow-lg">
        <div className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <Shield className="h-6 w-6 text-[#003366]" />
            <h2 className="text-xl font-semibold text-[#003366]">Web ACL Events</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Event Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    WebACL ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    WebACL Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Default Action
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Scope
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Rule Count
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Username
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Source IP
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Recipient Account ID
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {wafData?.data && wafData.data.length > 0 ? (
                  wafData.data.map((event, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900">
                        <LongTextCell text={event.eventname} />
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        <LongTextCell text={event.waf_id} />
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        <LongTextCell text={event.waf_name} />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {event.waf_default_action}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {event.waf_scope}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                        <RuleCountCell
                          count={event.waf_rule_count}
                          ruleNames={event.waf_rule_names}
                        />
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        <LongTextCell text={event.username} />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {event.sourceipaddress}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        <LongTextCell text={event.recipientaccountid} />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="9" className="px-6 py-10 text-center text-gray-500">
                      <div className="flex flex-col items-center justify-center">
                        <AlertTriangle className="h-10 w-10 text-gray-400 mb-2" />
                        <p className="text-lg font-medium">No data to display</p>
                        <p className="text-sm">No WAF events were found for the selected time period and filters.</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Rule Operations Table */}
      <div className="bg-white rounded-lg shadow-lg">
        <div className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <List className="h-6 w-6 text-[#003366]" />
            <h2 className="text-xl font-semibold text-[#003366]">Rule Operations Details</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Event Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Event Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Action
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Priority
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Web ACL ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Scope
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Username
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Source IP
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {ruleData && ruleData.length > 0 ? (
                  ruleData.map((event, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      {/* Table row content would go here */}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="10" className="px-6 py-10 text-center text-gray-500">
                      <div className="flex flex-col items-center justify-center">
                        <AlertTriangle className="h-10 w-10 text-gray-400 mb-2" />
                        <p className="text-lg font-medium">No data to display</p>
                        <p className="text-sm">No rule operations were found for the selected time period.</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Rule Group Operations Table */}
      <div className="bg-white rounded-lg shadow-lg">
        <div className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <Filter className="h-6 w-6 text-[#003366]" />
            <h2 className="text-xl font-semibold text-[#003366]">Rule Group Operations Details</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Event Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Event Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Scope
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Capacity
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Username
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Source IP
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {ruleGroupData && ruleGroupData.length > 0 ? (
                  ruleGroupData.map((event, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      {/* Table row content would go here */}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="10" className="px-6 py-10 text-center text-gray-500">
                      <div className="flex flex-col items-center justify-center">
                        <AlertTriangle className="h-10 w-10 text-gray-400 mb-2" />
                        <p className="text-lg font-medium">No data to display</p>
                        <p className="text-sm">No rule group operations were found for the selected time period.</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* API Key Operations Table */}
      <div className="bg-white rounded-lg shadow-lg">
        <div className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <Key className="h-6 w-6 text-[#003366]" />
            <h2 className="text-xl font-semibold text-[#003366]">API Key Operations Details</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Event Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Event Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Scope
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    API Key
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Username
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Source IP
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {apiKeyData && apiKeyData.length > 0 ? (
                  apiKeyData.map((event, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      {/* Table row content would go here */}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="px-6 py-10 text-center text-gray-500">
                      <div className="flex flex-col items-center justify-center">
                        <AlertTriangle className="h-10 w-10 text-gray-400 mb-2" />
                        <p className="text-lg font-medium">No data to display</p>
                        <p className="text-sm">No API key operations were found for the selected time period.</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WafPage;